import moment from 'moment-timezone';
import _ from 'lodash';
import Highcharts from 'highcharts';

import {
  getLegendName,
  getSeriesName,
} from '../../utils/chart/ChartManagementFeatures/getChartFeatures';
import { CSV_FULL_DATE_FORMAT, DATE_DAILY_SHORT_FORMAT } from '../../constants/date';
import getLineDefaultConfig from '../LineChartCommonConfig';
import { average, median } from '../../utils/common';
import { trendLine, trendLineConfig } from '../TrendLine';
import PeriodLineTooltip from '../../components/tooltip/period/PeriodLineTooltip';
import updateYaxisPlotLines from '../ChartPlotline';
import { getSortedSeries, toggleShowAllButton, updateYAxis } from '../Common';
import {
  calculateCumulative,
  hideCumulative,
  renderCumulativePaths,
  showCumulative,
} from '../../utils/chart/calculateCumulative';
import { sortingOrder, sortingTypes } from '../../constants/common';

const group = (data) => ({
  name: data.map(({ id, name }) => (id !== '' ? `${id}.${name}` : name)).join(' '),
  categories: _.get(
    data.find(({ categories }) => !!categories.length),
    'categories',
    []
  ),
  series: data.flatMap(({ name, series, timeUnit, periods, isSplitBy }) =>
    series.map((s) => ({
      ...s,
      periods,
      timeUnit,
      name: `${getSeriesName(s, name, s.parentId, isSplitBy)}`,
      isSplitBy,
    }))
  ),
});

const findSeriesCumulative = (data, name) =>
  data.find(({ id, showCumulative: isShowCumulative }) => id === name[0] && isShowCumulative);

const toPeriodLineConfig = (data, options = {}, showCalcTotal, fromDashboard, showSort) => {
  const { series } = group(data);

  const seriesSortOrder = sortingOrder.ASCENDING;
  const seriesSortType = sortingTypes.NAME;

  const seriesWithSortedLegend = getSortedSeries(series, seriesSortOrder, seriesSortType);

  const seriesData = seriesWithSortedLegend || series;

  const chartSeries = seriesData.map((s, index) => {
    const values = s.data.map(([, v]) => v);
    const averageValue = average(values);
    const { min, max } = trendLine(values, averageValue);
    const isHour = s.timeUnit === 'HOUR';
    const format = isHour ? CSV_FULL_DATE_FORMAT : DATE_DAILY_SHORT_FORMAT;

    return {
      id: index,
      periods: s.periods,
      isHour,
      name: `${s.name} (${s.periods})`,
      data: s.data.map(([, y, x]) => ({
        y,
        name: s.name.includes('Formula') ? '' : moment.tz(x, options.timezone).format(format),
        eventName: s.name,
        uniqueChar: s.parentId,
      })),
      tooltip: {
        headerFormat: '<strong>{point.key}</strong><br>',
        className: 'chart-tooltip',
      },
      uniqueChar: s.parentId,
      trendMin: min,
      trendMax: max,
      average: averageValue,
      isNegative: s.data.some((v) => v[1] < 0),
      type: 'line',
      color: s.color,
      marker: {
        symbol: s.legendSymbol,
      },
      legendIndex: s.legendIndex,
      dataSum: s.dataSum,
    };
  });

  // используется для отрисовки и удаления линии тренда
  let paths;

  let cumulativePaths = [];

  let yMax = 0;

  return {
    series: chartSeries,
    tooltip: {
      useHTML: true,
      split: true,
      distance: 30,
      formatter() {
        return PeriodLineTooltip(this, data);
      },
      shadow: false,
      borderWidth: 0,
      backgroundColor: 'rgba(255,255,255,0.8)',
    },
    ...getLineDefaultConfig(
      chartSeries,
      options,
      fromDashboard,
      data.some(({ showCumulative: isShowCumulative }) => isShowCumulative),
      showSort,
      seriesSortOrder,
      seriesSortType
    ),
    groups: ['median', 'average', 'trend', 'adaptive', options.group].concat(
      data.some(({ showCumulative: isShowCumulative }) => isShowCumulative) ? 'cumulative' : []
    ),
    chart: {
      zooming: {
        type: 'x',
      },
      spacingLeft: 0,
      marginLeft: 50,
      events: {
        redraw({ target: chart }) {
          updateYAxis(chart);
          if (!!chart.legend && chart.legend.title !== undefined) {
            chart.legend.title.attr('x', chart.legend.legendWidth / 2);
            chart.legend.title.attr('text-anchor', 'middle');
          }
          if (chart.series) {
            if (chart.showAll !== chart.series.every((s) => s.visible)) {
              toggleShowAllButton(chart);
            }

            // отфильтрованные серии графика, которые отображаются в данный момент
            const chartFilteredSeries = chart.series.filter((s) => s.visible);
            // id отфильтрованных серий
            const ids = chartFilteredSeries.map((s) => s.userOptions.id);
            const plotLines = [];
            // отфильтрованные серии взятые из подготовленного конфига, а не из самих графиков
            const filteredSeries = chartSeries.filter(({ id }) => ids.includes(id));

            // линия тренда
            if (paths) {
              paths.forEach(({ element }) => element.remove());
            }

            if (cumulativePaths) {
              cumulativePaths.forEach(({ element }) => element.remove());
            }

            if (chart.userOptions.trendLine) {
              const xAxis = chart.xAxis[0];
              const yAxis = chart.yAxis[0];
              paths = chartFilteredSeries.map(({ userOptions }, index) =>
                chart.renderer
                  .path([
                    'M',
                    xAxis.toPixels(xAxis.dataMin),
                    yAxis.toPixels(userOptions.trendMin),
                    'L',
                    xAxis.toPixels(xAxis.dataMax),
                    yAxis.toPixels(userOptions.trendMax),
                  ])
                  .attr({
                    ...trendLineConfig,
                    ...{
                      zIndex: 5,
                      stroke: chartFilteredSeries[index].color,
                    },
                  })
                  .add()
              );
            }

            if (chart.userOptions.cumulative) {
              const colorIndexes = [];
              const cumulativeLines = chartFilteredSeries
                .filter(({ name }, index) => {
                  const isCumulative = findSeriesCumulative(data, name);
                  if (isCumulative) {
                    colorIndexes.push(index);
                  }
                  return isCumulative;
                })
                .map(({ data: vData }) => {
                  const total = calculateCumulative(vData);
                  yMax = Math.max(yMax, total.at(-1).y);
                  return total;
                });

              if (!chart.userOptions.cumulativeShow) {
                showCumulative(chart, yMax);
              }

              cumulativePaths.forEach(({ element }) => element.remove());

              cumulativePaths = cumulativeLines.map((total, index) =>
                renderCumulativePaths(chart, total, chartFilteredSeries[colorIndexes[index]].color)
              );
            } else if (!chart.userOptions.cumulative && chart.userOptions.cumulativeShow) {
              hideCumulative(chart);
            }

            // plotLines
            if (filteredSeries.some((s) => s.isNegative)) {
              plotLines.push({
                id: 'zeroPlotLine',
                lineColor: 'black',
                dashStyle: 'solid',
                text: '',
                textColor: '',
                value: 0,
              });
            }
            if (chart.userOptions.averageLine) {
              const averages = filteredSeries.map(({ average: averageParam }) => averageParam);
              const averageValue = _.round(average(averages), 2);
              plotLines.push({
                id: 'averagePlotLine',
                lineColor: 'orange',
                dashStyle: 'dash',
                text: `Avg = ${averageValue}`,
                textColor: 'orange',
                value: averageValue,
              });
            }
            if (chart.userOptions.medianLine) {
              const resultData = filteredSeries.flatMap((d) => d.data.map(({ y }) => y));
              const medianValue = _.round(median(resultData) / filteredSeries.length, 2);
              plotLines.push({
                id: 'medianPlotLine',
                lineColor: 'red',
                dashStyle: 'solid',
                text: `Median = ${medianValue}`,
                textColor: 'red',
                value: medianValue,
              });
            }
            updateYaxisPlotLines(plotLines, chart);
          }
        },
      },
    },
    xAxis: {
      min: null,
      max: null,
      crosshair: {
        width: 1,
        color: 'black',
      },
      tickInterval: 1,
      labels: {
        formatter() {
          return `${data[0].timeUnit} ${this.value + 1}`;
        },
      },
      events: {
        afterSetExtremes() {
          Highcharts.charts.forEach((chart) => {
            if (chart?.userOptions.cumulativeShow) {
              const firstChar = chart.series[0]?.name[0];
              const isCurrent = data.find(({ id }) => id === firstChar);

              if (isCurrent) {
                chart.yAxis[0].update({ max: Math.max(yMax, chart.yAxis[0].dataMax) });
              }
            }
          });
        },
      },
    },
    legend: {
      title: {
        text: getLegendName(data, options?.legendName),
        style: {
          fontSize: '12px',
          fontWeight: 'bold',
        },
      },
      maxHeight: 117,
    },
  };
};

export default toPeriodLineConfig;
